import { useState, useMemo, useEffect, useRef } from "react";
import useAmplitude from "@/hooks/useAmplitude";
import {
  PDR_ANALYTICS_EVENTS,
  getParticipationFlowDurationSeconds,
  getAddParticipationFlowStartedAnalyticsData,
  getTypeLevelParticipationAnalyticsData,
  type PdrTypeLevelEntryPoint
} from "@/features/participation/_utils/amplitude";
import { Controller, useForm } from "react-hook-form";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import DateSelect from "@/components/DateSelect";
import Banner from "@/components/Banner";
import { useMutation } from "@apollo/client";
import { Person } from "@/types";
import PersonAutocomplete from "@/pages/activities/activity-types/[activityTypeId]/activity/[activityId]/add-participants/_components/PersonAutocomplete";
import { ActivityParticipationRole, AttendanceType, CreditRate } from "../../types";
import { CREATE_TYPE_LEVEL_PARTICIPATION, GET_PARTICIPANTS } from "../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { renderAttendanceType, renderParticipationRole, RATE_UNIT_MAP } from "../../_utils";
import type { RateUnit } from "../../types";
import classNames from "classnames";
import { uniqBy } from "lodash";

type FormValues = {
  participationLevel: string;
  participationRole: string;
  startDate: string;
  endDate: string;
  person: string;
  selectedPgaId: string;
  title: string;
  description: string;
  quantity: string;
};

const getAttendanceTypeOptions = (creditRates: CreditRate[]) =>
  uniqBy(creditRates, "attendanceType").map((cr) => ({
    value: cr.attendanceType,
    label: renderAttendanceType(cr.attendanceType),
    displayText: renderAttendanceType(cr.attendanceType)
  }));

const getRoleOptions = (creditRates: CreditRate[]) => {
  const roles =
    creditRates.length > 0
      ? uniqBy(creditRates, "participationRole").map((cr) => cr.participationRole)
      : (Object.values(ActivityParticipationRole) as ActivityParticipationRole[]);
  return roles.map((value) => ({
    value,
    label: renderParticipationRole(value),
    displayText: renderParticipationRole(value)
  }));
};

const getDefaultCreditRate = (creditRates: CreditRate[]) => creditRates[0];

const findSelectedCreditRate = (
  creditRates: CreditRate[],
  attendanceType?: AttendanceType | string,
  participationRole?: ActivityParticipationRole | string
) =>
  creditRates.find(
    (creditRate) => creditRate.attendanceType === attendanceType && creditRate.participationRole === participationRole
  );

const getDefaultFormValues = (
  creditRates: CreditRate[],
  preselectedPersonLabel?: string,
  preselectedPgaId?: string
): FormValues => {
  const today = new Date().toISOString().split("T")[0];
  const defaultCreditRate = getDefaultCreditRate(creditRates);

  return {
    participationLevel: defaultCreditRate?.attendanceType ?? AttendanceType.IN_PERSON,
    participationRole: defaultCreditRate?.participationRole ?? ActivityParticipationRole.ATTENDEE,
    startDate: today,
    endDate: "",
    person: preselectedPersonLabel ?? "",
    selectedPgaId: preselectedPgaId ?? "",
    title: "",
    description: "",
    quantity: ""
  };
};

export default function AddTypeLevelParticipationModal({
  onDismiss,
  activityTypeId,
  creditRates,
  onSuccess,
  preselectedPerson,
  preselectedPgaId,
  entryPoint = "activity_type_participants",
  participationFlowStartedAtMs
}: {
  onDismiss: () => void;
  activityTypeId: string;
  creditRates: CreditRate[];
  onSuccess?: () => void | Promise<unknown>;
  preselectedPerson?: Person;
  preselectedPgaId?: string;
  entryPoint?: PdrTypeLevelEntryPoint;
  /** When set, flow start was already tracked (e.g. person PD Add Participation). */
  participationFlowStartedAtMs?: number | null;
}) {
  const { Modal, close } = useModal();
  const { trackEvent } = useAmplitude();
  const flowStartedAtRef = useRef(participationFlowStartedAtMs ?? Date.now());
  const hasTrackedFlowStartRef = useRef(false);
  const [bannerError, setBannerError] = useState("");
  const isAddAnotherRef = useRef(false);
  const resolvedPreselectedPgaId = preselectedPerson?.pgaId || preselectedPgaId || "";
  const resolvedPreselectedPersonLabel =
    preselectedPerson &&
    (preselectedPerson.displayName || `${preselectedPerson.firstName} ${preselectedPerson.lastName}`.trim())
      ? preselectedPerson.displayName || `${preselectedPerson.firstName} ${preselectedPerson.lastName}`.trim()
      : resolvedPreselectedPgaId;
  const hasPreselectedPerson = !!resolvedPreselectedPgaId;

  const defaultFormValues = useMemo(
    () => getDefaultFormValues(creditRates, resolvedPreselectedPersonLabel, resolvedPreselectedPgaId),
    [creditRates, resolvedPreselectedPersonLabel, resolvedPreselectedPgaId]
  );

  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange"
  });

  const { control, watch, setValue, reset: resetForm } = form;

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const selectedPgaId = watch("selectedPgaId");
  const selectedAttendanceType = watch("participationLevel");
  const selectedParticipationRole = watch("participationRole");
  const title = watch("title");
  const quantity = watch("quantity");

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setValue("endDate", startDate);
    }
  }, [startDate, endDate, setValue]);

  useEffect(() => {
    if (!creditRates.length) return;

    const selectedCreditRate = findSelectedCreditRate(creditRates, selectedAttendanceType, selectedParticipationRole);
    if (selectedCreditRate) return;

    const nextCreditRate =
      creditRates.find((creditRate) => creditRate.attendanceType === selectedAttendanceType) ||
      creditRates.find((creditRate) => creditRate.participationRole === selectedParticipationRole) ||
      getDefaultCreditRate(creditRates);

    if (!nextCreditRate) return;

    if (selectedAttendanceType !== nextCreditRate.attendanceType) {
      setValue("participationLevel", nextCreditRate.attendanceType);
    }

    if (selectedParticipationRole !== nextCreditRate.participationRole) {
      setValue("participationRole", nextCreditRate.participationRole);
    }
  }, [creditRates, selectedAttendanceType, selectedParticipationRole, setValue]);

  const selectedCreditRate = useMemo(
    () => findSelectedCreditRate(creditRates, selectedAttendanceType, selectedParticipationRole),
    [creditRates, selectedAttendanceType, selectedParticipationRole]
  );

  const rateUnit = selectedCreditRate?.rateUnit;
  const rateMultiplier = selectedCreditRate?.rateMultiplier;
  const rateUnitLabel = rateUnit ? RATE_UNIT_MAP[rateUnit as RateUnit] : "unit";
  const hideQuantity = rateUnit === "INSTANCE" && rateMultiplier !== 1;

  const attendanceTypeOptions = useMemo(() => getAttendanceTypeOptions(creditRates), [creditRates]);
  const roleOptions = useMemo(() => getRoleOptions(creditRates), [creditRates]);

  useEffect(() => {
    if (hasTrackedFlowStartRef.current) return;
    hasTrackedFlowStartRef.current = true;

    if (participationFlowStartedAtMs != null) {
      flowStartedAtRef.current = participationFlowStartedAtMs;
      return;
    }

    flowStartedAtRef.current = Date.now();
    trackEvent(
      PDR_ANALYTICS_EVENTS.addParticipationStarted,
      getAddParticipationFlowStartedAnalyticsData({
        source: entryPoint,
        participationKind: "type-level"
      })
    );
  }, [entryPoint, participationFlowStartedAtMs, trackEvent]);

  const [createParticipation, { loading }] = useMutation(CREATE_TYPE_LEVEL_PARTICIPATION, {
    refetchQueries: [GET_PARTICIPANTS],
    onCompleted: async (data) => {
      const result = data?.createActivityTypeParticipation;
      if (result?.success) {
        trackEvent(
          PDR_ANALYTICS_EVENTS.typeLevelParticipationCreated,
          getTypeLevelParticipationAnalyticsData({
            source: entryPoint,
            activityTypeId,
            durationSeconds: getParticipationFlowDurationSeconds(flowStartedAtRef.current)
          })
        );
        showSnackbar(result.message ?? "Participation created", { close: true });
        if (!isAddAnotherRef.current) {
          close();
          onDismiss();
        } else {
          resetForm(defaultFormValues);
          flowStartedAtRef.current = Date.now();
        }
        isAddAnotherRef.current = false;
        await onSuccess?.();
      } else {
        setBannerError(result?.message ?? "Failed to create participation.");
        isAddAnotherRef.current = false;
      }
    },
    onError: (error) => {
      isAddAnotherRef.current = false;
      setBannerError(error.message ?? "Failed to create participation.");
    }
  });

  const parsedQuantity = hideQuantity ? 1 : parseFloat(quantity);
  const canSave =
    !!selectedCreditRate &&
    !!selectedPgaId &&
    !!title.trim() &&
    (hideQuantity || (!!quantity && !isNaN(parsedQuantity) && parsedQuantity > 0)) &&
    !!startDate;

  const handleSubmit = async () => {
    if (!canSave) return;
    setBannerError("");
    const effectiveEndDate = endDate || startDate;
    await createParticipation({
      variables: {
        activityTypeId,
        pgaId: selectedPgaId,
        startDate: `${startDate}T00:00:00Z`,
        endDate: `${effectiveEndDate}T00:00:00Z`,
        quantity: parsedQuantity,
        title: title.trim(),
        description: form.getValues("description").trim() || undefined,
        participationRole: form.getValues("participationRole"),
        attendanceType: form.getValues("participationLevel")
      }
    });
  };

  const handleSaveAndAddAnother = async () => {
    isAddAnotherRef.current = true;
    await handleSubmit();
  };

  const selectPerson = (name: string, value: Person, setValueFn: (name: string, value: string) => void) => {
    setValue("selectedPgaId", value.pgaId || "");
    setValueFn(name, `${value.firstName} ${value.lastName}`);
  };

  return (
    <Modal
      onClosed={() => {
        resetForm(defaultFormValues);
        onDismiss();
      }}
      actions={
        <div className="flex flex-1 justify-between w-full gap-2">
          <Button
            variant="text"
            onClick={handleSaveAndAddAnother}
            disabled={hasPreselectedPerson || !canSave || loading}
            testId="type-level-add-another-button"
          >
            Save and Add Another
          </Button>
          <div className="flex gap-2">
            <Button variant="text" onClick={close} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleSubmit}
              disabled={!canSave || loading}
              icon={loading ? "progress_activity" : undefined}
              iconClassName={classNames(loading && "animate-spin w-6 h-6 !block")}
              testId="type-level-save-button"
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      }
      headline="Add Participant"
      hasFormElements
      displayOverflow
      className="h-fit max-h-[90vh] md:w-[490px]"
    >
      <div className="space-y-6">
        {!!bannerError && (
          <Banner inline variant="error" description={bannerError} closable onClose={() => setBannerError("")} />
        )}

        <p className="text-body-medium text-sys-brand-on-surface-variant">
          Adds a participation for one person without creating an activity.
        </p>

        {hasPreselectedPerson ? (
          <TextField
            label="Person"
            value={resolvedPreselectedPersonLabel}
            variant="outlined"
            className="w-full"
            readOnly
            disabled
          />
        ) : (
          <Controller
            name="person"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <PersonAutocomplete
                label="Person"
                required
                query={value}
                onChangeText={onChange}
                reset={() => {
                  onChange("");
                  setValue("selectedPgaId", "");
                }}
                onSelect={(person: Person) => selectPerson(name, person, (n, v) => setValue(n as keyof FormValues, v))}
                autofocus
              />
            )}
          />
        )}

        <Controller
          name="title"
          control={control}
          rules={{ required: "Title is required" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              label="Title"
              value={value}
              onChangeText={onChange}
              required
              variant="outlined"
              className="w-full"
              error={!!error}
              errorText={error?.message}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Description"
              value={value}
              onChangeText={onChange}
              variant="outlined"
              className="w-full"
            />
          )}
        />

        {!hideQuantity && (
          <Controller
            name="quantity"
            control={control}
            rules={{
              required: "Quantity is required",
              validate: (v) => {
                const num = parseFloat(v);
                if (isNaN(num) || num <= 0) return "Must be a positive number";
                if (v.includes(".") && (v.split(".")[1]?.length ?? 0) > 1) return "Up to one decimal place";
                return true;
              }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={rateUnit === "INSTANCE" ? "PDR Credits" : `Quantity (${rateUnitLabel}s)`}
                value={value}
                onChangeText={onChange}
                required
                variant="outlined"
                className="w-full"
                type="number"
                error={!!error}
                errorText={error?.message}
              />
            )}
          />
        )}

        {attendanceTypeOptions.length > 1 && (
          <Controller
            name="participationLevel"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                label="Attendance Type"
                required
                options={attendanceTypeOptions}
                value={value}
                onSelect={(v) => onChange(v as AttendanceType)}
              />
            )}
          />
        )}

        {roleOptions.length > 1 && (
          <Controller
            name="participationRole"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                label="Participation Role"
                required
                options={roleOptions}
                value={value}
                onSelect={(v) => onChange(v as ActivityParticipationRole)}
              />
            )}
          />
        )}

        <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
          <Controller
            name="startDate"
            control={control}
            rules={{ required: "Start Date is required" }}
            render={({ field, fieldState }) => (
              <DateSelect
                label="Start Date *"
                value={{ startDate: field.value || "", endDate: field.value || "" }}
                useSingleDate
                useModal
                onChange={(value) => {
                  if (value === null || typeof value === "string") {
                    field.onChange("");
                    return;
                  }
                  const selected = value.startDate;
                  field.onChange(selected instanceof Date ? selected.toISOString().split("T")[0] : (selected ?? ""));
                }}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            render={({ field, fieldState }) => (
              <DateSelect
                label="End Date"
                value={{ startDate: field.value || "", endDate: field.value || "" }}
                useSingleDate
                useModal
                minDate={startDate ? new Date(`${startDate}T00:00:00`) : undefined}
                onChange={(value) => {
                  if (value === null || typeof value === "string") {
                    field.onChange("");
                    return;
                  }
                  const selected = value.startDate;
                  field.onChange(selected instanceof Date ? selected.toISOString().split("T")[0] : (selected ?? ""));
                }}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
}
